import { useEffect, useState } from 'react';
import type { Loan, LoanEvent } from '@/types';
import { listEvents } from '@/lib/db';
import { getLoanStatusLabel } from '@/lib/calc/loanState';
import { LoanForm } from './components/LoanForm';
import { LoanStatusBadge } from '@/components/StatusBadge';

interface SettingsPageProps {
  loan: Loan;
  onLoanUpdated: () => void;
}

const DAY_COUNT_LABELS: Record<Loan['dayCountConvention'], string> = {
  actual_365_fixed: 'Actual / 365 (fixed)',
  actual_365_25: 'Actual / 365.25',
  actual_360: 'Actual / 360',
};

const PRE_EMI_LABELS: Record<Loan['preEmiInterestTreatment'], string> = {
  pay_as_you_go: 'Pay as you go (interest-only during pre-EMI)',
  capitalize_at_emi_start: 'Capitalized at EMI start (added to principal)',
};

const ROUNDING_LABELS: Record<Loan['emiRoundingRule'], string> = {
  nearest_paisa: 'Nearest paisa (2 decimal places)',
  nearest_rupee: 'Nearest rupee',
  nearest_ten_rupees: 'Nearest ₹10',
};

export function SettingsPage({ loan, onLoanUpdated }: SettingsPageProps) {
  const [events, setEvents] = useState<LoanEvent[]>([]);
  useEffect(() => {
    let cancelled = false;
    listEvents({ loanId: loan.id }).then((result) => {
      if (!cancelled) setEvents(result);
    });
    return () => {
      cancelled = true;
    };
  }, [loan.id]);
  const statusInfo = getLoanStatusLabel(loan, events);

  return (
    <div className="stack">
      <div>
        <h1>Loan Settings</h1>
        <LoanStatusBadge status={loan.status} label={statusInfo.label} isDraft={statusInfo.isDraft} />
      </div>

      <div className="card" style={{ borderColor: 'var(--color-warning)' }}>
        <div className="card-title">LIC-Specific Assumptions (Pending Verification)</div>
        <p>
          These settings control how the calculation engine computes interest. They are this app's current
          assumptions, not confirmed LIC HFL behaviour — update them once you can verify against a real LIC
          statement or interest certificate.
        </p>
        <div className="stack" style={{ gap: 10 }}>
          <Row
            label="Repayment Assumption"
            value={
              loan.repaymentArrangement === 'interest_only_then_regular'
                ? 'Interest-only, then regular EMI'
                : loan.repaymentArrangement === 'regular_from_start'
                  ? 'Regular EMI from start date'
                  : 'Automatic (estimate)'
            }
          />
          <Row label="Day-Count Convention" value={DAY_COUNT_LABELS[loan.dayCountConvention]} />
          <Row label="Pre-EMI Interest Treatment" value={PRE_EMI_LABELS[loan.preEmiInterestTreatment]} />
          <Row label="EMI Rounding Rule" value={ROUNDING_LABELS[loan.emiRoundingRule]} />
        </div>
        <p className="field-hint" style={{ marginTop: 12 }}>
          Day-count and pre-EMI treatment can be changed in the form below. The EMI rounding rule is currently fixed
          at its documented default.
        </p>
      </div>

      <div className="card">
        <div className="card-title">Edit Loan Details</div>
        <LoanForm existingLoan={loan} onSaved={onLoanUpdated} />
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span className="text-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
